import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  UserAddOutlined,
  LogoutOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  SafetyCertificateOutlined,
  CrownOutlined,
  StopOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import toast from "react-hot-toast";
import moment from "moment";

import { getAllUsers, updateUser, deleteUser } from "../actions/users";
import ProfileImage from "../ui/Avatar";
import { PERMISSIONS } from "../constants/userAccess";
import { signOut } from "../Api/index";

const { Title, Text } = Typography;

function getInitials(user) {
  const first = user?.firstName?.[0] || "";
  const last = user?.lastName?.[0] || "";
  return `${first}${last}`.toUpperCase() || "U";
}

// `login` means "signed in to one of the apps right now", not account status.
function StatusPill({ login }) {
  const active = login === "yes";

  return (
    <Tag
      color={active ? "green" : "red"}
      style={{
        fontWeight: 800,
        borderRadius: 999,
        padding: "2px 8px",
        fontSize: 11,
        lineHeight: "18px",
      }}
    >
      {active ? "ONLINE" : "OFFLINE"}
    </Tag>
  );
}

function BlinkingDot({ active }) {
  if (!active) return null;

  return (
    <span
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: 999,
        background: "#22c55e",
        marginLeft: 5,
        boxShadow: "0 0 0 4px rgba(34, 197, 94, .12)",
        animation: "blink 1s infinite",
      }}
    />
  );
}

function PermissionText({ value }) {
  const color =
    value === "Administrator"
      ? "purple"
      : value === "Read-Write"
        ? "blue"
        : value === "Approver"
          ? "cyan"
          : value === "Payer"
            ? "geekblue"
            : value === "Read Only"
              ? "gold"
              : "default";

  return (
    <Tag
      color={color}
      style={{
        borderRadius: 999,
        fontWeight: 700,
        padding: "1px 7px",
        fontSize: 11,
        lineHeight: "18px",
      }}
    >
      {value || "Restricted"}
    </Tag>
  );
}

function StatCard({ title, value, tone, icon }) {
  const config = {
    info: {
      bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    success: {
      bg: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
      border: "#bbf7d0",
      color: "#15803d",
    },
    danger: {
      bg: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
      border: "#fecdd3",
      color: "#be123c",
    },
    dark: {
      bg: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      border: "#334155",
      color: "#ffffff",
    },
  };

  const c = config[tone] || config.info;

  return (
    <Card
      bordered
      style={{
        borderRadius: 16,
        background: c.bg,
        borderColor: c.border,
        height: "100%",
        boxShadow: "0 10px 24px rgba(15, 23, 42, .06)",
      }}
      bodyStyle={{ padding: "9px 11px" }}
    >
      <Space
        align="center"
        style={{ width: "100%", justifyContent: "space-between" }}
      >
        <div>
          <Text
            style={{
              fontWeight: 900,
              color: tone === "dark" ? "#cbd5e1" : "#475569",
              fontSize: 11,
              letterSpacing: 0.35,
              textTransform: "uppercase",
            }}
          >
            {title}
          </Text>

          <Title
            level={4}
            style={{
              margin: 0,
              marginTop: 1,
              fontWeight: 950,
              color: c.color,
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            {value}
          </Title>
        </div>

        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 11,
            background:
              tone === "dark"
                ? "rgba(255,255,255,.12)"
                : "rgba(255,255,255,.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: c.color,
            fontSize: 15,
          }}
        >
          {icon}
        </div>
      </Space>
    </Card>
  );
}

export default function UserManagementAntdFull() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const auth = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"));
    } catch {
      return null;
    }
  }, []);

  const currentUser = auth?.results || {};
  const usersData = useSelector((state) => state.users || []);
  const usersLoading = useSelector((state) => state.usersLoading);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 30 });
  const [users, setUsers] = useState(usersData);

  useEffect(() => setUsers(usersData), [usersData]);

  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");

  const branchFilters = React.useMemo(
    () =>
      [...new Set(users.map((user) => user.branch).filter(Boolean))]
        .sort()
        .map((branch) => ({ text: branch.toUpperCase(), value: branch })),
    [users],
  );

  const activeUsers = React.useMemo(
    () => users.filter((user) => user.login === "yes"),
    [users],
  );

  const inactiveUsers = React.useMemo(
    () => users.filter((user) => user.login === "no"),
    [users],
  );

  const adminUsers = React.useMemo(
    () =>
      users.filter((user) =>
        PERMISSIONS.some(
          (permission) => user?.[permission.key] === "Administrator",
        ),
      ),
    [users],
  );

  const filteredUsers = React.useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return users;

    return users.filter((user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`
        .trim()
        .toLowerCase();

      const email = (user.email || "").toLowerCase();
      const branch = (user.branch || "").toLowerCase();
      const position = (user.position || "").toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        branch.includes(query) ||
        position.includes(query)
      );
    });
  }, [users, searchText]);

  const goToAddUser = () => {
    navigate("/admin/users/add");
  };

  const goToEditUser = (record) => {
    navigate(`/admin/users/edit/${record._id}`);
  };

  // The actions toast their own errors; a failed request just skips the reload.
  const suspendUser = async (record) => {
    try {
      await dispatch(
        updateUser(
          record._id,
          {
            ...PERMISSIONS.reduce((acc, permission) => {
              acc[permission.key] = "Restricted";
              return acc;
            }, {}),
            hr_permission: "no",
            payroll_permission: "no",
            login: "no",
            revokeSessions: true,
          },
          "User suspended",
        ),
      );
      dispatch(getAllUsers());
    } catch (error) {}
  };

  const forceLogoutUser = async (record) => {
    try {
      await dispatch(
        updateUser(
          record._id,
          { login: "no", revokeSessions: true },
          "User logged out",
        ),
      );
      dispatch(getAllUsers());
    } catch (error) {}
  };

  const deleteUserNow = async (record) => {
    try {
      await dispatch(deleteUser(record._id));
      dispatch(getAllUsers());
    } catch (error) {}
  };

  const logout = () => {
    const id = toast.loading("Please wait we are logging you out...");

    // Ends the shared login, so the other apps are logged out too
    signOut().catch(() => {});
    dispatch({ type: "LOGOUT" });

    setTimeout(() => {
      toast.remove(id);
      toast.success("You are out, see you again!");
      navigate("/admin/login");
    }, 1200);

    setLogoutOpen(false);
  };

  const columns = [
    {
      title: "#",
      width: 55,
      fixed: "left",
      render: (_, __, index) => (
        <span
          style={{
            fontWeight: 900,
            color: "#475569",
            fontSize: 12,
          }}
        >
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </span>
      ),
    },
    {
      title: "User",
      dataIndex: "firstName",
      width: 260,
      fixed: "left",
      render: (_, record) => (
        <Space size={9}>
          <Avatar
            size={36}
            style={{
              background: "linear-gradient(135deg, #1677ff, #4096ff)",
              fontWeight: 900,
              fontSize: 12,
              boxShadow: "0 10px 20px rgba(22, 119, 255, .22)",
            }}
          >
            {getInitials(record)}
          </Avatar>

          <div>
            <Text
              strong
              style={{
                color: "#0f172a",
                fontSize: 12,
                lineHeight: 1.2,
              }}
            >
              {record.firstName || "-"} {record.lastName || ""}
            </Text>

            <div>
              <Text type="secondary" style={{ fontSize: 10.5 }}>
                {record.email || "No email"}
              </Text>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "login",
      width: 120,
      filters: [
        { text: "Online", value: "yes" },
        { text: "Offline", value: "no" },
      ],
      onFilter: (value, record) => (record.login || "no") === value,
      render: (value) => <StatusPill login={value} />,
    },
    {
      title: "Branch",
      dataIndex: "branch",
      width: 150,
      filters: branchFilters,
      filterSearch: true,
      onFilter: (value, record) => record.branch === value,
      render: (value) => (
        <Tag
          icon={<EnvironmentOutlined />}
          style={{
            borderRadius: 999,
            padding: "2px 8px",
            fontWeight: 700,
            fontSize: 11,
            lineHeight: "18px",
          }}
        >
          {value || "—"}
        </Tag>
      ),
    },
    {
      title: "Position",
      dataIndex: "position",
      width: 170,
      render: (value) => <span style={{ fontSize: 12 }}>{value || "—"}</span>,
    },

    ...PERMISSIONS.map((permission) => ({
      title: permission.label,
      dataIndex: permission.key,
      width: permission.width,
      render: (value, record) => {
        const systemActive =
          (record.system || "").toLowerCase() === permission.key.toLowerCase();

        return (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <PermissionText value={value} />
            <BlinkingDot active={systemActive} />
          </span>
        );
      },
    })),

    {
      title: "HR Right",
      dataIndex: "hr_permission",
      width: 110,
      render: (value) => (
        <Tag
          color={value === "yes" ? "green" : "default"}
          style={{
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 11,
            lineHeight: "18px",
          }}
        >
          {(value || "no").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Payroll Right",
      dataIndex: "payroll_permission",
      width: 135,
      render: (value) => (
        <Tag
          color={value === "yes" ? "green" : "default"}
          style={{
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 11,
            lineHeight: "18px",
          }}
        >
          {(value || "no").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 205,
      render: (_, record) => {
        // Admins can't delete, suspend or log out their own account
        const isSelf = record._id === currentUser?._id;

        return (
          <Space size={5}>
            <Tooltip title="Edit user">
              <Button
                size="small"
                icon={<EditOutlined />}
                aria-label="Edit user"
                onClick={() => goToEditUser(record)}
                style={{
                  borderRadius: 9,
                  fontWeight: 700,
                  width: 28,
                  height: 28,
                }}
              />
            </Tooltip>

            <Popconfirm
              title={`Delete ${record.firstName || "this user"} ${record.lastName || ""}?`}
              description="This permanently removes the account."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteUserNow(record)}
              disabled={isSelf}
            >
              <Tooltip
                title={isSelf ? "You can't delete yourself" : "Delete user"}
              >
                <Button
                  size="small"
                  danger
                  disabled={isSelf}
                  aria-label="Delete user"
                  icon={<DeleteOutlined />}
                  style={{
                    borderRadius: 9,
                    width: 28,
                    height: 28,
                  }}
                />
              </Tooltip>
            </Popconfirm>

            <Popconfirm
              title={`Suspend ${record.firstName || "this user"}?`}
              description="Sets every platform to Restricted and signs them out."
              okText="Suspend"
              onConfirm={() => suspendUser(record)}
              disabled={isSelf}
            >
              <Tooltip
                title={isSelf ? "You can't suspend yourself" : "Suspend user"}
              >
                <Button
                  size="small"
                  disabled={isSelf}
                  icon={<LockOutlined />}
                  aria-label="Suspend user"
                  style={{
                    borderRadius: 9,
                    width: 28,
                    height: 28,
                  }}
                />
              </Tooltip>
            </Popconfirm>

            <Popconfirm
              title={`Log out ${record.firstName || "this user"}?`}
              description="Ends their sessions on every app."
              okText="Logout"
              onConfirm={() => forceLogoutUser(record)}
              disabled={isSelf}
            >
              <Tooltip title={isSelf ? "Use Logout above" : "Logout user"}>
                <Button
                  size="small"
                  disabled={isSelf}
                  aria-label="Log out user"
                  icon={<LogoutOutlined />}
                  style={{
                    borderRadius: 9,
                    width: 28,
                    height: 28,
                  }}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "65px 18px 100px",
        background:
          "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
        fontSize: 12,
      }}
    >
      <style>
        {`
          @keyframes blink { 
            0%, 100% { opacity: 1 } 
            50% { opacity: .25 } 
          }

          .premium-users-table .ant-table {
            border-radius: 20px;
            overflow: hidden;
            font-size: 12.5px;
          }

          .premium-users-table .ant-table-thead > tr > th {
            background: #f8fafc !important;
            color: #334155 !important;
            font-weight: 900 !important;
            font-size: 11px !important;
            text-transform: uppercase;
            letter-spacing: .25px;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
          }

          .premium-users-table .ant-table-tbody > tr > td {
            padding-top: 7px !important;
            padding-bottom: 7px !important;
            font-size: 12.5px !important;
          }

          .premium-users-table .ant-table-tbody > tr:hover > td {
            background: #eff6ff !important;
          }

          .premium-users-table .ant-pagination {
            font-size: 11.5px;
          }

          .premium-users-table .ant-select,
          .premium-users-table .ant-pagination-options,
          .premium-users-table .ant-pagination-total-text {
            font-size: 11.5px;
          }

          .premium-users-search input {
            font-weight: 600;
            font-size: 12px;
          }

          .premium-users-search .ant-input-prefix {
            font-size: 12px;
          }

          .premium-users-page .ant-btn {
            font-size: 12px;
          }

          .premium-users-page .ant-tag {
            font-size: 11px;
          }

          .premium-users-page .ant-modal-title {
            font-size: 14px;
          }

          .premium-users-page .ant-popconfirm-title,
          .premium-users-page .ant-popconfirm-description {
            font-size: 12px;
          }
        `}
      </style>

      <div
        className="premium-users-page"
        style={{ maxWidth: 1480, margin: "0 auto" }}
      >
        <Card
          bordered={false}
          style={{
            borderRadius: 26,
            overflow: "hidden",
            boxShadow: "0 22px 55px rgba(15, 23, 42, .10)",
            marginBottom: 18,
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              padding: 16,
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#fff",
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} lg={11}>
                <Space size={12} align="center">
                  <div
                    style={{
                      borderRadius: 22,
                      padding: 4,
                      background: "rgba(255,255,255,.12)",
                    }}
                  >
                    <ProfileImage />
                  </div>

                  <div>
                    <Title
                      level={3}
                      style={{
                        margin: 0,
                        color: "#fff",
                        fontWeight: 950,
                        fontSize: 21,
                        lineHeight: 1.15,
                      }}
                    >
                      Hello {currentUser?.firstName || "Admin"}!
                    </Title>

                    <Text style={{ color: "#cbd5e1", fontSize: 12 }}>
                      Manage users, permissions and platform access.
                    </Text>

                    <div style={{ marginTop: 7 }}>
                      <Space wrap size={6}>
                        <Tag
                          color="blue"
                          style={{
                            borderRadius: 999,
                            padding: "2px 9px",
                            fontWeight: 800,
                            fontSize: 11,
                            lineHeight: "18px",
                          }}
                        >
                          Role: {currentUser?.userRole || "—"}
                        </Tag>

                        <Tag
                          icon={<EnvironmentOutlined />}
                          style={{
                            borderRadius: 999,
                            padding: "2px 9px",
                            fontWeight: 800,
                            fontSize: 11,
                            lineHeight: "18px",
                          }}
                        >
                          {(currentUser?.branch || "No branch").toUpperCase()}
                        </Tag>
                      </Space>
                    </div>

                    <div style={{ marginTop: 5 }}>
                      <Text style={{ color: "#94a3b8", fontSize: 10.5 }}>
                        Joined:{" "}
                        {currentUser?.createdAt
                          ? moment(currentUser.createdAt).format("DD-MM-YYYY")
                          : "—"}{" "}
                        {currentUser?.createdAt
                          ? `| ${moment(currentUser.createdAt).fromNow()}`
                          : ""}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={13}>
                <Row gutter={[10, 10]}>
                  <Col xs={12} md={6}>
                    <StatCard
                      title="TOTAL USERS"
                      value={users.length}
                      tone="dark"
                      icon={<TeamOutlined />}
                    />
                  </Col>

                  <Col xs={12} md={6}>
                    <StatCard
                      title="ONLINE NOW"
                      value={activeUsers.length}
                      tone="success"
                      icon={<UserSwitchOutlined />}
                    />
                  </Col>

                  <Col xs={12} md={6}>
                    <StatCard
                      title="OFFLINE"
                      value={inactiveUsers.length}
                      tone="danger"
                      icon={<StopOutlined />}
                    />
                  </Col>

                  <Col xs={12} md={6}>
                    <StatCard
                      title="ADMIN ACCESS"
                      value={adminUsers.length}
                      tone="info"
                      icon={<CrownOutlined />}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          <div
            style={{
              padding: "11px 17px",
              background: "rgba(255,255,255,.92)",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Space wrap size={8}>
              <Tag
                icon={<SafetyCertificateOutlined />}
                color="blue"
                style={{
                  borderRadius: 999,
                  padding: "3px 10px",
                  fontWeight: 800,
                  fontSize: 11,
                  lineHeight: "18px",
                }}
              >
                User Access Control
              </Tag>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Edit details from the dedicated user edit page.
              </Text>
            </Space>

            <Space wrap size={8}>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={usersLoading}
                onClick={() => dispatch(getAllUsers())}
                style={{
                  borderRadius: 10,
                  fontWeight: 700,
                  height: 30,
                }}
              >
                Reload
              </Button>

              <Button
                size="small"
                type="primary"
                icon={<UserAddOutlined />}
                onClick={goToAddUser}
                style={{
                  borderRadius: 10,
                  fontWeight: 800,
                  height: 30,
                  boxShadow: "0 10px 20px rgba(22, 119, 255, .22)",
                }}
              >
                Add New User
              </Button>

              <Button
                size="small"
                danger
                icon={<LogoutOutlined />}
                onClick={() => setLogoutOpen(true)}
                style={{
                  borderRadius: 10,
                  fontWeight: 700,
                  height: 30,
                }}
              >
                Logout
              </Button>
            </Space>
          </div>
        </Card>

        <Card
          bordered={false}
          style={{
            borderRadius: 15,
            boxShadow: "0 22px 55px rgba(15, 23, 42, .08)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
              background: "#fff",
            }}
          >
            <div>
              <Title
                level={4}
                style={{
                  margin: 0,
                  fontWeight: 950,
                  fontSize: 17,
                  lineHeight: 1.2,
                }}
              >
                Users Directory
              </Title>

              <Text type="secondary" style={{ fontSize: 12 }}>
                Search by name, email, branch, or position.
              </Text>
            </div>

            <Space wrap size={8}>
              <Input
                allowClear
                className="premium-users-search"
                prefix={<SearchOutlined />}
                placeholder="Search by name, email, branch..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                style={{
                  width: 320,
                  borderRadius: 10,
                  height: 34,
                  fontSize: 12,
                }}
              />

              <Tag
                color="geekblue"
                style={{
                  borderRadius: 999,
                  padding: "4px 12px",
                  fontWeight: 900,
                  fontSize: 11,
                  lineHeight: "18px",
                }}
              >
                {filteredUsers.length} / {users.length} RECORDS
              </Tag>
            </Space>
          </div>

          <Table
            className="premium-users-table"
            rowKey="_id"
            columns={columns}
            dataSource={filteredUsers}
            loading={usersLoading}
            onChange={(next) =>
              setPagination({
                current: next.current,
                pageSize: next.pageSize,
              })
            }
            size="small"
            bordered={false}
            sticky
            scroll={{ x: 1900, y: 590 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
            }}
          />
        </Card>
      </div>

      <Modal
        title="Logout"
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onOk={logout}
        okText="Yes, logout"
        okButtonProps={{ danger: true }}
      >
        <Text style={{ fontSize: 12 }}>Are you sure you want to logout?</Text>
      </Modal>
    </div>
  );
}
